import React, { useMemo } from 'react';
import { BtnPrimary } from '../common/Buttons';
import { generateInvoice } from '../../hooks/mutations';
import toast from 'react-hot-toast';
import { useFieldArray, useForm } from 'react-hook-form';
import { FormInputs, invoiceSchema } from '../../validations/invoice';
import { zodResolver } from '@hookform/resolvers/zod';
import { useThemeStore } from '../../state/theme';
import { IoAddCircleOutline } from 'react-icons/io5';
import { FaTrashAlt } from 'react-icons/fa';
import {
  fetchAgentList,
  fetchAgentLogs,
  getAgentBrand,
} from '../../hooks/queries';
import { useCalenderState } from '../../state/calender-state';
import moment from 'moment-timezone';
import { IBrand } from '../../types/brand';
import { getActivePayment } from '../../utils';

const GenerateInvoice: React.FC<{
  agentId: string;
  account: string;
  month: string;
  year: string;
  selectedBrand?: IBrand;
}> = ({ account, agentId, month, year, selectedBrand }) => {
  const { selectedTimezoneName } = useCalenderState();
  const {
    data: agentLogs,
    isLoading: isAgentLogsLoading,
    isFetching: isAgentLogsFetching,
    isError: isAgentLogsError,
  } = fetchAgentLogs({
    account,
    agentId,
    month,
    year,
    offset: moment.tz(selectedTimezoneName).utcOffset(),
  });

  const {
    data: agents,
    isLoading: isAgentsLoading,
    isFetching: isAgentsFetching,
    isError: isAgentsError,
  } = fetchAgentList();

  const agent = agents?.agents?.find((agent) => agent.kimId === agentId);

  const {
    data: agentBrand,
    isLoading: isAgentBrandLoading,
    isFetching: isAgentBrandFetching,
    isError: isAgentBrandError,
  } = getAgentBrand(agent?._id || '', selectedBrand?._id || '', {
    enabled: !!agent?._id,
  });

  const isLogsLoaded =
    !isAgentLogsLoading &&
    !isAgentLogsFetching &&
    agentLogs &&
    !isAgentsLoading &&
    !isAgentsFetching &&
    !isAgentBrandLoading &&
    !isAgentBrandFetching;

  const isError = isAgentLogsError || isAgentsError || isAgentBrandError;

  const { isDarkMode } = useThemeStore();

  const {
    mutate: generateInvoiceMutation,
    isPending: isGenerateInvoicePending,
  } = generateInvoice({
    onSuccess: (data) => {
      const url = new URL(data.invoiceUrl).href;
      window.open(url, '_blank');
    },
    onError: (error) => toast.error(error.message),
  });

  const totalDuration =
    (agentLogs?.data?.reduce((acc, log) => {
      const work = log?.duration || 0;
      const breaks =
        log?.breaks?.reduce((bAcc, brk) => bAcc + (brk?.duration || 0), 0) || 0;

      return acc + work + breaks;
    }, 0) || 0) / 60;

  const agentBrandData = agentBrand?.data?.[0];

  const { agentRate, currency, totalAmount } = useMemo(() => {
    const activePayment = getActivePayment(agentBrandData?.payment);
    if (!activePayment?.toAgent) {
      return {
        agentRate: 0,
        currency: 'USD',
        totalAmount: 0,
      };
    }

    const rate = activePayment.toAgent?.amount || 0;
    const curr = activePayment.toAgent?.currency || 'USD';
    const totAmount = totalDuration * rate;

    return {
      agentRate: rate,
      currency: curr,
      totalAmount: totAmount,
    };
  }, [agentBrandData, totalDuration]);

  const form = useForm<FormInputs>({
    resolver: zodResolver(invoiceSchema),
    defaultValues: {
      arrears: 0,
      bonuses: Array.isArray(agentBrandData?.bonus) ? agentBrandData.bonus : [],
    },
  });

  const {
    register,
    formState: { errors },
  } = form;

  const {
    fields: bonusFields,
    append: appendBonus,
    remove: removeBonus,
  } = useFieldArray({
    control: form.control,
    name: 'bonuses',
  });

  const onSubmit = (data: FormInputs) =>
    generateInvoiceMutation({
      account,
      agentId,
      date: `${year}-${month}`,
      additionalDetails: data,
    });

  const inputClassName = `w-full rounded-md px-3 py-2 shadow-sm focus:outline-none ${
    isDarkMode
      ? 'bg-gray-700 border-gray-600 text-gray-100 focus:border-indigo-500 focus:ring-indigo-500'
      : 'border border-gray-300 focus:border-indigo-500 focus:ring-indigo-500'
  }`;

  const labelClassName = `block text-sm font-medium mb-1 ${
    isDarkMode ? 'text-gray-300' : 'text-gray-700'
  }`;

  return isLogsLoaded ? (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.preventDefault(); // Prevent form submission on Enter key
      }}
      className="space-y-6 flex flex-col"
    >
      <div className="space-y-2">
        <label className={labelClassName}>Work Log Details</label>
        <div
          className={`flex items-center gap-2 p-4 ${
            isDarkMode ? 'border border-white text-white' : 'bg-gray-100'
          } rounded-lg shadow-sm`}
        >
          <div className="w-full">
            <p className="text-sm font-medium dark:text-gray-300 mb-2">
              Total Hours:{' '}
              <span className="font-bold dark:text-white">
                {totalDuration.toFixed(2)}
              </span>
            </p>
            <p className="text-sm font-medium dark:text-gray-300 mb-2">
              Hourly Rate:{' '}
              <span className="font-bold dark:text-white">
                {currency} {agentRate}
              </span>
            </p>
            <p className="text-sm font-medium dark:text-gray-300">
              Total Amount:{' '}
              <span className="font-bold dark:text-white">
                {currency} {totalAmount.toFixed(2)}
              </span>
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center gap-2">
          {bonusFields.length > 0 && (
            <label className={labelClassName}>Bonuses</label>
          )}
          <button
            type="button"
            onClick={() =>
              appendBonus({ amount: 0, name: '', frequency: 'weekly' })
            }
            className={`flex items-center gap-1 ${isDarkMode ? 'text-white hover:text-indigo-500' : 'text-blue-600 hover:text-blue-800'}`}
          >
            <IoAddCircleOutline className="h-5 w-5" />
            {bonusFields.length <= 0 && 'Add Bonus'}
          </button>
        </div>
        {bonusFields.map((field, index) => (
          <div key={field.id} className="flex items-start gap-2 rounded-md">
            <input
              type="text"
              {...register(`bonuses.${index}.name`)}
              className={inputClassName}
              placeholder="Name"
            />
            <input
              type="number"
              {...register(`bonuses.${index}.amount`, {
                valueAsNumber: true,
              })}
              className={inputClassName}
              placeholder="Amount"
            />

            <select
              {...register(`bonuses.${index}.frequency`)}
              className={inputClassName}
            >
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="daily">Daily</option>
              <option value="fortnightly">Fortnightly</option>
              <option value="one-time">One Time</option>
            </select>
            <button
              type="button"
              onClick={() => removeBonus(index)}
              className="text-red-500 hover:text-red-700 mt-2"
            >
              <FaTrashAlt className="h-5 w-5" />
            </button>
          </div>
        ))}
        {errors.bonuses && (
          <span className="text-red-500 text-sm">{errors.bonuses.message}</span>
        )}
      </div>

      <div className="space-y-4">
        <label className={labelClassName}>Arrears</label>
        <input
          type="number"
          {...register('arrears', { valueAsNumber: true })}
          className={inputClassName}
        />
      </div>

      <BtnPrimary
        disabled={isGenerateInvoicePending}
        className="self-end"
        type="submit"
      >
        {isGenerateInvoicePending ? 'Generating...' : 'Generate'}
      </BtnPrimary>
    </form>
  ) : isError ? (
    <p>Error loading data</p>
  ) : (
    <p>Please wait...</p>
  );
};

export default GenerateInvoice;
